// Putting people on a campaign's call list.
//
// This is the operation that makes the dialler usable at all — before it, a
// cpass campaign could only be filled by hand-written SQL.
//
// Extracted from AIVoiceCampaigns so the list and the detail view queue through
// exactly the same code. Two copies of this would be two chances to drop the
// de-duplication, and dropping it means calling people twice.
import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { voiceCampaignsService, VoiceCampaign, BusinessNumber } from 'api/services/voiceCampaigns.service';
import RecipientSources, { RecipientSelection, dedupeKey } from './RecipientSources';
import { looksInternational } from './recipientRules';
import { lifecycleStates, normaliseStatus } from './campaignDetailRules';

interface Props {
  campaign: VoiceCampaign;
  onClose: () => void;
  /** Called after a successful queue, so the caller can refresh its counts. */
  onQueued?: () => void;
  inputClass: string;
  labelClass: string;
}

/**
 * Reads back what is already scheduled, up to a cap.
 *
 * Without this, queueing a list onto a campaign that already carries it dials
 * everyone twice — and the engine will not refuse, because a second attempt at
 * the same number is exactly what a retry looks like to it.
 */
const ALREADY_PAGES = 10, ALREADY_SIZE = 500;

const QueueRecipientsModal: React.FC<Props> = ({ campaign, onClose, onQueued, inputClass, labelClass }) => {
  const [selection, setSelection] = useState<RecipientSelection>({ recipients: [], duplicates: 0, invalid: 0 });
  const [callInitTime, setCallInitTime] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alreadyQueued, setAlreadyQueued] = useState<Set<string> | null>(null);
  const [alreadyNote, setAlreadyNote] = useState<string | null>(null);

  // THE NUMBER THESE CALLS WILL PRESENT.
  //
  // It belongs here and not on the campaign form, for a cpass-dialled campaign:
  // that engine refuses caller_id on update — "a cpass campaign keeps its agent
  // and caller id on its recipients, set when they were queued" — and the queue
  // endpoint has always accepted one per request. Without a field the only
  // source was the campaign's default, so a campaign created without one could
  // never be given a caller ID at all, by any route in this portal.
  //
  // Prefilled from the campaign so the ordinary case is untouched; editable so
  // one list can present a different number without a second campaign existing
  // for it.
  const campaignCallerId = (campaign as any).defaultCallerId || (campaign as any).callerId || '';
  const [callerId, setCallerId] = useState<string>(campaignCallerId);
  const [numbers, setNumbers] = useState<BusinessNumber[]>([]);
  const [numbersNote, setNumbersNote] = useState<string | null>(null);
  // The CAMPAIGN's setting, not a choice made here. Whoever tops up a list is
  // often not whoever decided the policy, and a tick box on this screen let the
  // uploader override it by accident. Changed on the campaign itself; the
  // backend enforces it either way, so this only decides what is SHOWN.
  const allowRepeats = campaign.allowDuplicateRecipients === true;

  const id = campaign.id || '';
  const status = normaliseStatus(campaign.status);

  const loadAlreadyQueued = useCallback(async () => {
    if (!id) return;
    setAlreadyQueued(null); setAlreadyNote(null);
    const keys = new Set<string>();
    let truncated = false;
    for (let p = 0; p < ALREADY_PAGES; p++) {
      const res = await voiceCampaignsService.listRecipients(id, p, ALREADY_SIZE);
      if (!res.success) {
        setAlreadyNote('Could not read what is already queued — duplicates will not be filtered.');
        return;
      }
      const d: any = res.data || {};
      const rows: any[] = d.content || d.data || [];
      rows.forEach(r => { if (r?.callTo) keys.add(dedupeKey(String(r.callTo))); });
      const last = d.last ?? (rows.length < ALREADY_SIZE);
      if (last) break;
      if (p === ALREADY_PAGES - 1) truncated = true;
    }
    setAlreadyQueued(keys);
    if (truncated) {
      setAlreadyNote(`Checked the first ${(ALREADY_PAGES * ALREADY_SIZE).toLocaleString()} already queued; ` +
        'anything beyond that is not filtered.');
    }
  }, [id]);

  useEffect(() => { loadAlreadyQueued(); }, [loadAlreadyQueued]);

  // The same list the create form offers, from the same endpoint, so the two
  // screens cannot disagree about what this business is allowed to present.
  // Not fatal if it fails: the field falls back to being typed, which is the
  // only way a number the runtime does not list can still be used.
  useEffect(() => {
    voiceCampaignsService.numbers().then(r => {
      if (r.success) setNumbers(r.data || []);
      else setNumbersNote(r.error || 'Could not load numbers from the runtime');
    });
  }, []);

  // Split before sending so the count on the button is the count that dials.
  // With repeats allowed nothing is withheld, but the count is still computed
  // and shown — an operator re-calling a list should see how many of them have
  // been called by this campaign before.
  const wouldRepeat = selection.recipients.filter(r => alreadyQueued?.has(dedupeKey(r.phone))).length;
  const fresh = allowRepeats
    ? selection.recipients
    : selection.recipients.filter(r => !alreadyQueued?.has(dedupeKey(r.phone)));

  // A caller ID the runtime does not offer — see the option that renders it.
  const orphanCallerId = callerId && !numbers.some(n => n.phoneNumber === callerId)
    ? callerId
    : '';

  const queue = async () => {
    if (!id) return;
    if (!fresh.length) {
      setFailed(false);
      setResult(allowRepeats ? 'Nothing to queue.' : 'Nothing new to queue.');
      return;
    }
    // Refused here rather than at the API, because the backend's refusal arrives
    // after the upload and reads as a campaign fault: "No caller ID is
    // configured for this campaign". The row it would write is undialable from
    // the moment it exists — three such rows once became three ORIGINATE_FAILED
    // 400s, hours later.
    if (!callerId.trim()) {
      setFailed(true);
      setResult('A caller ID is needed — the switch refuses a call with no number to present, '
        + 'so none of these recipients could be dialled.');
      return;
    }

    setBusy(true); setResult(null); setFailed(false);
    const res = await voiceCampaignsService.addRecipients(id, fresh, {
      caller_id: callerId.trim(),
      call_init_time: callInitTime ? new Date(callInitTime).toISOString() : undefined,
    });
    setBusy(false);
    if (!res.success) { setFailed(true); setResult(res.error || 'Queue failed'); return; }
    const d: any = res.data || {};
    // Report what the engine accepted, not what we sent.
    setResult(`Accepted ${d.accepted ?? 0} of ${d.submitted ?? fresh.length}`
      + `${d.skipped ? ` — ${d.skipped} skipped as blank` : ''}.`);
    loadAlreadyQueued();
    onQueued?.();
  };

  // A campaign that has ended will never dial what is added now. The engine
  // accepts the rows regardless, which is exactly why the warning belongs here.
  const ended = status === 'COMPLETED' || status === 'ARCHIVED';
  const running = lifecycleStates(status).pause.enabled;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
      <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Queue recipients</h2>
            <p className="text-[12.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {campaign.campaignName || '(unnamed)'}
            </p>
          </div>
          <button className="tl-icon-btn" onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button>
        </div>

        {ended && (
          <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg"
               style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
            This campaign has ended. Anything queued now is accepted and stored, but nothing will dial it.
          </div>
        )}
        {running && (
          <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg"
               style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
            This campaign is running. Whatever you queue here becomes dialable straight away.
          </div>
        )}

        <div className="mt-4">
          <RecipientSources onChange={setSelection} inputClass={inputClass} labelClass={labelClass}
                            allowRepeats={allowRepeats} />
        </div>

        {/* Already on the campaign. Shown whether or not it changes the count,
            because "0 repeats" is itself the reassurance someone topping up a
            live campaign is looking for. */}
        {(wouldRepeat > 0 || selection.duplicates > 0 || alreadyNote) && (
          <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg"
               style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
            {wouldRepeat > 0 && (allowRepeats
              ? <>{wouldRepeat.toLocaleString()} of these have already been queued on this campaign and <strong>will be called again</strong>. </>
              : <>{wouldRepeat.toLocaleString()} already queued on this campaign and will not be queued again. </>)}
            {selection.duplicates > 0 && (allowRepeats
              ? <>{selection.duplicates.toLocaleString()} repeated within this list and will be called once per entry. </>
              : <>{selection.duplicates.toLocaleString()} repeated within this list and were collapsed. </>)}
            {alreadyNote}
          </div>
        )}

        {/* Not editable here on purpose — see the note on allowRepeats above.
            Stated plainly so somebody queueing a list knows which rule is in
            force before they press the button, rather than after. */}
        <div className="mt-3 text-[12px]" style={{ color: 'var(--text-muted)' }}>
          {allowRepeats
            ? <>This campaign <strong>allows the same number more than once</strong>. Every entry
              becomes a call, including numbers it has already dialled.</>
            : <>This campaign queues each number <strong>once</strong>. Repeats are skipped.</>}
          {' '}Change it on the campaign to switch.
        </div>
        {alreadyQueued === null && !alreadyNote && (
          <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading what is already queued…
          </div>
        )}

        <div className="mt-3">
          <label className={labelClass}>Caller ID</label>
          {numbers.length > 0 ? (
            <select className={inputClass} value={callerId}
                    onChange={e => setCallerId(e.target.value)}>
              <option value="">— select a number —</option>
              {numbers.map(n => (
                <option key={n.phoneNumber} value={n.phoneNumber}>
                  {n.phoneNumber}{n.friendlyName ? ` — ${n.friendlyName}` : ''}
                  {n.numberType ? ` (${n.numberType})` : ''}
                </option>
              ))}
              {/* The campaign's own number, when the runtime does not list it —
                  it may have been set directly, or have since stopped being
                  allocated. Without this option a <select> silently falls back
                  to the blank one and the prefill is lost without a word. */}
              {orphanCallerId && (
                <option value={orphanCallerId}>{orphanCallerId} — set on the campaign</option>
              )}
            </select>
          ) : (
            <input type="text" className={inputClass + ' font-mono'} value={callerId}
                   onChange={e => setCallerId(e.target.value)}
                   placeholder="00919228034171" />
          )}
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {numbers.length === 0
              ? (numbersNote
                  ? `The number these calls present. No number list available — ${numbersNote}. Enter one manually.`
                  : 'The number these calls present. No numbers are allocated to this business on the runtime; enter one manually.')
              : campaignCallerId
                ? 'The number these calls present. From the campaign — change it to use a different one for this list only.'
                : 'The number these calls present. This campaign has none set, so it must be chosen here.'}
          </p>
          {/* Nothing downstream adds a country code: a bare ten-digit number
              passes every check and then fails at the switch with Allocation
              failed. A warning rather than a block — this cannot know every
              dial plan. */}
          {callerId.trim() && !looksInternational(callerId) && (
            <p className="text-[11px] mt-1.5 rounded px-2 py-1.5
                          bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              That looks like a national number. Nothing adds a country code later, and the switch
              refuses a call it cannot route — try <code>00</code> or <code>+</code> and the country
              code.
            </p>
          )}
        </div>

        <div className="mt-3">
          <label className={labelClass}>Start dialling at <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
          <input type="datetime-local" className={inputClass} value={callInitTime}
                 onChange={e => setCallInitTime(e.target.value)} />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
            Leave blank to dial as soon as the campaign is started.
          </p>
        </div>

        {result && (
          <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg"
               style={failed
                 ? { background: 'var(--danger-bg)', color: 'var(--danger-text)' }
                 : { background: 'var(--success-bg)', color: 'var(--success-text)' }}>
            {result}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button className="tl-btn tl-btn-neutral" onClick={onClose}>Close</button>
          <button className="tl-btn tl-btn-primary" onClick={queue}
                  disabled={busy || fresh.length === 0 || alreadyQueued === null}>
            {busy ? 'Queueing…' : `Queue ${fresh.length.toLocaleString()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueueRecipientsModal;
